import _isEmpty from "lodash/isEmpty";
import { requiresCreditCheck as requiresCreditCheckSelector } from "./creditCheckSelectors";
import { getRetailerState } from "./selectors";
import { PartialPick, VERIFICATION_TYPE } from "../../../../../constants";
import { RootState } from "../../../../../models";
import {
    BankIdUser,
    CustomerCreditCheck,
    CustomerExistingAccount,
    CustomerExistingSubscription,
    CustomerIdentification,
    CustomerNewSubscriptionIcims,
    LegalOwnerIndividual,
    LegalOwnerOrganization,
} from "../../../../../models/state";

export type State = PartialPick<RootState, "retailer">;

export const getExistingPhoneNumberLookup = (state: State): string | undefined =>
    getRetailerState(state)?.customer?.existingPhoneNumberLookup;

export const getCustomerExistingSubscriptions = (state: State): CustomerExistingSubscription[] =>
    getRetailerState(state)?.customer?.existingSubscriptions || [];

export const getCustomerExistingAccounts = (state: State): CustomerExistingAccount[] =>
    getRetailerState(state)?.customer?.existingAccounts || [];

export const getCustomerSelectedAccountId = (state: State): string | undefined =>
    getRetailerState(state)?.customer?.selectedAccountId;

export const getExistingSubscriptionForChange = (state: State): CustomerExistingSubscription | undefined =>
    getRetailerState(state)?.customer?.existingSubscriptionForChange;

export const getReopenExistingSubscriptionPhone = (state: State): string | undefined =>
    getRetailerState(state)?.customer?.reopenExistingSubscriptionPhone;

export const getCustomerNewSubscriptionsIcim = (state: State): CustomerNewSubscriptionIcims =>
    getRetailerState(state)?.customer?.newSubscriptionIcims || { mbb: [], mhs: [], prepaid: [], vas: [] };

export const getCustomerLegalOwnerIndividual = (state: State): Partial<LegalOwnerIndividual> | null => {
    const legalOwner = getRetailerState(state)?.customer?.legalOwner;
    if (!legalOwner) return null;
    return legalOwner.individual ?? (legalOwner.organization ? null : legalOwner);
};

export const getCustomerLegalOwnerOrganization = (state: State): Partial<LegalOwnerOrganization> | null => {
    const legalOwner = getRetailerState(state)?.customer?.legalOwner;
    return legalOwner?.organization ?? null;
};

export const getCustomerCreditCheck = (state: State): CustomerCreditCheck | undefined =>
    getRetailerState(state)?.customer?.verification?.creditCheck;

export const getCustomerLookupPerformed = (state: State): boolean =>
    getRetailerState(state)?.customer?.verification?.manual?.performed || false;

export const getCustomerManualResult = (state: State) =>
    getRetailerState(state)?.customer?.verification?.manual?.result;

/**
 * Whether the customer's SSN has been registered, i.e. either a manual
 * customer lookup or a credit check has been performed. This is distinct
 * from identification (BankID/manual ID verification of the person).
 */
export const hasCustomerLookupOrCreditCheckBeenPerformed = (state: State): boolean =>
    getCustomerLookupPerformed(state) || !!getCustomerCreditCheck(state)?.performed;

export const getCustomerBankId = (state: State): Partial<CustomerIdentification> => {
    const verification = getRetailerState(state)?.customer?.verification;
    if (verification?.identification?.type === VERIFICATION_TYPE.BANK_ID) {
        return verification.identification;
    }

    return {};
};

export const shouldPollBankId = (state: State): boolean => !!getCustomerBankId(state)?.shouldPoll;

export const getBankIdResult = (state: State): BankIdUser | undefined => getCustomerBankId(state)?.customer;

export const getCustomerIdentification = (state: State) =>
    getRetailerState(state)?.customer?.verification?.identification || {};

export const hasVerifiedIdentification = (state: State): boolean => {
    const bankIdPerformed = getCustomerBankId(state)?.performed || false;
    const otherIdentificationPerformed = getCustomerIdentification(state)?.performed || false;
    return bankIdPerformed || otherIdentificationPerformed;
};

export const hasVerifiedBankId = (state: State): boolean => {
    const bankIdResult = getBankIdResult(state);
    return !!bankIdResult && !_isEmpty(bankIdResult);
};

export const getIsPrepaid = (state: State): boolean => getRetailerState(state)?.customer?.isPrepaid || false;

export const getCustomerContactInfo = (state: State) => getRetailerState(state)?.customer?.contactInfo;

export const isCustomerStepComplete = (state: State): boolean => {
    const hasVerified = hasVerifiedIdentification(state);
    const legalOwner = getCustomerLegalOwnerIndividual(state);
    const legalOwnerOrganization = getCustomerLegalOwnerOrganization(state);
    const creditCheck = getCustomerCreditCheck(state);
    const existingAccounts = getCustomerExistingAccounts(state);
    const isExistingCustomer = existingAccounts.length > 0;
    const requiresCreditCheck = requiresCreditCheckSelector(state);
    const hasOrganizationData = !!(legalOwnerOrganization?.name || legalOwnerOrganization?.number);
    const hasLegalOwner = hasOrganizationData
        ? !!legalOwnerOrganization?.name && !!legalOwnerOrganization.number
        : !_isEmpty(legalOwner?.firstName);
    const contactInfo = getCustomerContactInfo(state);
    const hasContactInfoSaved = !!contactInfo?.email;
    const hasLookedUpCustomer = getCustomerLookupPerformed(state);

    // For existing customers: allow to proceed after customer lookup (identification)
    // Credit check will be performed later if selected offering requires it
    if (isExistingCustomer) {
        return hasVerified && hasLegalOwner && (hasLookedUpCustomer || !!creditCheck?.performed);
    }

    // For new customers: require credit check to be completed
    return !!(
        hasVerified &&
        hasContactInfoSaved &&
        (requiresCreditCheck
            ? creditCheck?.performed &&
              creditCheck.decision !== "DENIED" &&
              creditCheck.decision !== "SCORE_NOT_AVAILABLE" &&
              creditCheck.decision !== "INVALID_NATIONAL_IDENTITY_NUMBER"
            : hasLegalOwner)
    );
};
